/**
 * src/components/public/layout/RevealFooter.tsx
 *
 * Scroll-reveal footer sarmalayıcı (kullanıcı talebi 2026-05-31, "havalı footer").
 *
 * Efekt: footer ekranın altında SABİT (z-0, arkada) durur; içerik (header+main)
 * opak zeminli ve üstte (z-10), altında footer yüksekliği kadar margin bırakır.
 * Sayfa sonuna kaydırınca içerik yukarı kayar ve footer alttan "ortaya çıkar".
 *
 * Yalnızca lg+ (masaüstü): footer yüksekliği < viewport olduğunda doğru çalışır.
 * Mobilde footer uzun (stacked kolonlar) → viewport'u aşar → reveal bozulur;
 * bu yüzden mobilde footer NORMAL akışta (static) kalır, margin uygulanmaz.
 *
 * Footer yüksekliği ResizeObserver ile ölçülür → içerik margin-bottom'u CSS
 * değişkeni (--reveal-footer-h) ile beslenir (responsive: yalnız lg:mb).
 *
 * Client Component — ölçüm (ref + ResizeObserver) gerektirir.
 */
'use client'

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

interface RevealFooterProps {
  /** İçerik (SiteHeader + main). Opak zeminli üst katman. */
  children: ReactNode
  /** Footer (SiteFooter). lg+'da sabit arka katman. */
  footer: ReactNode
}

/**
 * RevealFooter — içerik + footer'ı scroll-reveal düzeninde sarmalar.
 *
 * @param children Header + main (üst, opak, z-10)
 * @param footer SiteFooter (lg+ sabit, z-0, arkada)
 */
export function RevealFooter({ children, footer }: RevealFooterProps) {
  const footerRef = useRef<HTMLDivElement>(null)
  const [footerH, setFooterH] = useState(0)

  // Footer yüksekliğini ölç → içerik margin-bottom'u olarak kullan. Tema/dil/
  // viewport değişiminde footer yüksekliği değişebilir; ResizeObserver canlı tutar.
  useEffect(() => {
    const el = footerRef.current
    if (!el) return
    const measure = () => setFooterH(el.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <>
      {/* Üst katman — opak zemin footer'ı kapatır; lg'de altta reveal boşluğu. */}
      <div
        className="relative z-10 flex min-h-screen flex-col bg-white lg:mb-[var(--reveal-footer-h)] dark:bg-slate-950"
        style={{ '--reveal-footer-h': `${footerH}px` } as CSSProperties}
      >
        {children}
      </div>

      {/* Footer — mobil: normal akış (static). lg+: viewport altına sabit, arkada. */}
      <div ref={footerRef} className="z-0 lg:fixed lg:inset-x-0 lg:bottom-0">
        {footer}
      </div>
    </>
  )
}
